import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import moment from "moment";
import {
  IconMessages,
  IconInbox,
  IconHeadset,
  IconEdit,
  IconSearch,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
  IconArrowForwardUp,
  IconTrash,
  IconArrowLeft,
  IconMail,
  IconCircleCheck,
  IconCornerUpLeft,
  IconInboxOff,
  IconAlertCircle,
} from "@tabler/icons-react";
import classes from "./ClientMessagesRedesign.module.css";
import { confirmationHandler } from "../../../utils/confirmationHandler";
import { QuillToolbar, modules, formats } from "../../../utils/editorToolbar";
import BottomTools from "../../../utils/bottomToolbar";
import { DocumentHeader } from "../../../components/Seo";
import {
  clientCreateOrGetConversation,
  clientDeleteConversation,
  clientGetConversations,
  clientGetMessages,
  clientSendMessage,
  clientUpdateMessageStatus,
  getSupportComplaintById,
  getSupportComplaints,
  postSupportComplaint,
  replySupportComplaint,
} from "../../../services/clientCalls";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import DOMPurify from "dompurify";
import { errorHandler } from "../../../utils";
import { useSearchParams } from "react-router-dom";
import {
  emailFromQueryParam,
  isAllowedMessageRecipientEmail,
  TALENT_EMAIL_FORMAT_ERROR,
} from "../../../utils/functions";

const PAGE_SIZE = 8;

const emptyCopy = {
  user: {
    title: "No messages yet",
    body: "Conversations will show up here once one starts.",
  },
  support: {
    title: "Your inbox is empty",
    body: "Messages from our support team will appear here.",
  },
};

const Editor = ({ value, onChange, placeholder }) => {
  const quillRef = useRef(null);
  return (
    <div className={classes.editorWrap}>
      <QuillToolbar quillRef={quillRef} />
      <ReactQuill
        ref={quillRef}
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

const ClientMessagesRedesign = () => {
  const [inbox, setInbox] = useState("user");
  const [view, setView] = useState("list");

  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  // Both tab badges stay accurate regardless of which inbox is open.
  const [userTotal, setUserTotal] = useState(0);
  const [supportTotal, setSupportTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const [composeTo, setComposeTo] = useState("");
  const [composeToError, setComposeToError] = useState("");
  const [composeCategory, setComposeCategory] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [searchParams] = useSearchParams();
  const emailParam = emailFromQueryParam(searchParams.get("email"));

  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2600);
  };

  const fetchSupportComplaints = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await getSupportComplaints(pageNum, PAGE_SIZE);
      const data = res?.data;
      const list =
        data?.data?.records ||
        data?.records ||
        (Array.isArray(data?.data) ? data.data : []) ||
        [];
      const term = search.trim().toLowerCase();
      const mapped = list?.map(formatSupportComplaint) || [];
      const filtered = term
        ? mapped.filter(
            (item) =>
              item?.subject?.toLowerCase()?.includes(term) ||
              item?.lastMessage?.toLowerCase()?.includes(term),
          )
        : mapped;
      const resolvedTotal =
        data?.data?.totalItems ??
        data?.data?.counts ??
        data?.data?.pagination?.total ??
        filtered.length;
      setConversations(filtered);
      setTotal(resolvedTotal);
      setSupportTotal(mapped.length);
    } catch (_) {
      setConversations([]);
      setTotal(0);
      setSupportTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (pageNum = 1) => {
    if (inbox === "support") {
      fetchSupportComplaints(pageNum);
      return;
    }
    setLoading(true);
    try {
      const res = await clientGetConversations(
        pageNum,
        PAGE_SIZE,
        search.trim() || undefined,
      );
      const data = res?.data;
      if (data?.success || data?.status === "success") {
        const list = data?.data?.conversations || data?.conversations || [];
        const resolvedTotal =
          data?.data?.pagination?.total ?? data.data?.total ?? list.length;
        setConversations(list.map(formatConversation));
        setTotal(resolvedTotal);
        setUserTotal(resolvedTotal);
      } else {
        setConversations([]);
        setTotal(0);
        setUserTotal(0);
      }
    } catch (_) {
      setConversations([]);
      setTotal(0);
      setUserTotal(0);
    } finally {
      setLoading(false);
    }
  };

  /* Support complaints come back in a different shape to conversations, so we
     normalise them onto the same fields the table and thread view already use. */
  const supportBodyOf = (complaint) => complaint?.message || "";

  const supportRepliesOf = (complaint) => {
    const replies = [
      ...(complaint?.replies || []).map((reply) => ({
        ...reply,
        fromSupport: true,
      })),
      ...(complaint?.repliesToAdminSupport || []).map((reply) => ({
        ...reply,
        fromSupport: false,
      })),
    ];
    return replies.sort(
      (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
    );
  };

  const formatSupportComplaint = (complaint) => {
    const replies = supportRepliesOf(complaint);
    const lastReply = replies.length ? replies[replies.length - 1] : null;
    return {
      _id: complaint?._id,
      isSupport: true,
      raw: complaint,
      subject: complaint?.title || complaint?.supportType || "Support request",
      // adminSupportStatus is the progress of the request ("In-action" etc);
      // `status` on the record is just read/unread.
      status: complaint?.adminSupportStatus || "",
      lastMessage:
        lastReply?.message || supportBodyOf(complaint) || "No messages yet",
      lastMessageAt:
        lastReply?.createdAt || complaint?.updatedAt || complaint?.createdAt,
      latestMessages: [],
      unread: complaint?.status !== "read",
      user: {
        name: "Awasource Support",
        email: "support@awasource.com",
        type: "Support",
      },
    };
  };

  const formatConversation = (conversation) => {
    const latest = conversation?.messages || conversation?.latestMessages || [];
    const lastMessage = latest.length
      ? latest.reduce((a, b) =>
          new Date(b?.createdAt) > new Date(a?.createdAt) ? b : a,
        )
      : null;
    return {
      _id: conversation?._id,
      subject: conversation?.subject || "No subject",
      lastMessage:
        lastMessage?.content || conversation?.lastMessage || "No messages yet",
      lastMessageAt:
        lastMessage?.createdAt ||
        conversation?.lastMessageAt ||
        conversation?.createdAt,
      latestMessages: latest,
      unread: conversation?.hasUnread ?? conversation?.unread ?? false,
      user: {
        name:
          conversation?.participantName ||
          `${conversation?.talentId?.firstName || ""} ${conversation?.talentId?.lastName || ""}`.trim() ||
          "Talent",
        email:
          conversation?.participantEmail ||
          conversation?.talentId?.email ||
          "Email not available",
        type: conversation?.initiatedBy === "Talent" ? "Client" : "Talent",
      },
    };
  };

  // Load the support count once on mount so its tab badge is right even while
  // the user inbox is the one on screen.
  useEffect(() => {
    (async () => {
      try {
        const res = await getSupportComplaints(1, PAGE_SIZE);
        const d = res?.data;
        setSupportTotal(
          d?.data?.totalItems ?? (d?.data?.records || []).length ?? 0,
        );
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    setPage(1);
    setSortKey(null);
    fetchConversations(1);
    setView("list");
    setSelectedConversation(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inbox]);

  useEffect(() => {
    fetchConversations(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!emailParam) return;
    setView("compose");
    if (isAllowedMessageRecipientEmail(emailParam)) {
      setComposeTo(emailParam);
      setComposeToError("");
    } else {
      setComposeTo("");
      setComposeToError(TALENT_EMAIL_FORMAT_ERROR);
    }
  }, [emailParam]);



  const sortedConversations = [...conversations].sort((a, b) => {
    if (!sortKey) return 0;
    let aValue, bValue;
    if (sortKey === "date") {
      aValue = new Date(a?.lastMessageAt || 0).getTime();
      bValue = new Date(b?.lastMessageAt || 0).getTime();
      return (aValue - bValue) * sortDir;
    }
    if (sortKey === "title") {
      aValue = a?.subject;
      bValue = b?.subject;
    } else if (sortKey === "user") {
      aValue = a?.user?.name;
      bValue = b?.user?.name;
    } else if (sortKey === "type") {
      aValue = a?.user?.type;
      bValue = b?.user?.type;
    }
    return String(aValue || "").localeCompare(String(bValue || "")) * sortDir;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d * -1);
    else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <IconSelector size={13} />;
    return sortDir === 1 ? (
      <IconChevronUp size={13} />
    ) : (
      <IconChevronDown size={13} />
    );
  };

  /* ── Thread ── */
  /* Builds the thread for one support request: the message the client raised,
     followed by each reply from the support team, newest first. */
  const buildSupportThread = (complaint) => {
    const you = { firstName: "You", lastName: "", email: "" };
    const supportUser = {
      firstName: "Awasource",
      lastName: "Support",
      email: "support@awasource.com",
    };
    const adminStarted = complaint?.supportType === "AdminSupport";
    const original = {
      _id: complaint?._id || complaint?.id,
      createdAt: complaint?.createdAt,
      content: supportBodyOf(complaint),
      sender: adminStarted ? supportUser : you,
      receiver: adminStarted ? you : supportUser,
      senderType: adminStarted ? "Support" : "Client",
      isSentByMe: !adminStarted,
    };
    const replies = supportRepliesOf(complaint)?.map((reply, index) => {
      const fromSupport = reply?.fromSupport !== false && Boolean(reply?.adminId || reply?.adminName);
      return {
        _id: reply?._id || `${complaint?._id || "support"}-reply-${index}`,
        createdAt: reply?.createdAt || complaint?.updatedAt,
        content: reply?.message || "",
        sender: fromSupport ? supportUser : you,
        receiver: fromSupport ? you : supportUser,
        senderType: fromSupport ? "Support" : "Client",
        isSentByMe: !fromSupport,
      };
    });
    return [original, ...replies]
      .filter((m) => m?.content)
      .sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
  };

  const openSupportThread = async (complaint) => {
    setSelectedConversation(complaint);
    setView("thread");
    setShowReply(false);
    setReplyBody("");
    setLoading(true);
    try {
      const res = await getSupportComplaintById(complaint._id);
      const data = res?.data;
      const detail =
        data?.data?.supportEntry || data?.supportEntry || complaint?.raw;
      setMessages(buildSupportThread(detail || complaint?.raw || {}));
    } catch (_) {
      // Fall back to what the list already gave us rather than showing nothing.
      setMessages(buildSupportThread(complaint?.raw || {}));
    } finally {
      setLoading(false);
    }
  };

  const openThread = async (conversation, autoReply = false) => {
    if (conversation?.isSupport) {
      openSupportThread(conversation);
      return;
    }
    setSelectedConversation(conversation);
    setView("thread");
    setShowReply(autoReply);
    setReplyBody("");
    setLoading(true);
    try {
      const res = await clientGetMessages(conversation._id, 1);
      if (res?.status === "success") {
        const list =
          res?.data?.messages || res?.data?.data?.messages || [];
        const sorted = [...list]?.sort(
          (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
        );
        setMessages(sorted);
        sorted?.forEach((msg) => {
          if (msg?.senderType === "Talent" && msg?.status !== "read") {
            clientUpdateMessageStatus(msg._id, "read").catch(() => {});
          }
        });
      } else {
        setMessages(conversation.latestMessages || []);
      }
    } catch (_) {
      setMessages(conversation.latestMessages || []);
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    setView("list");
    setSelectedConversation(null);
    setMessages([]);
    setShowReply(false);
    fetchConversations(page);
  };

  const getOtherParticipant = () => {
    const other =
      messages.find((m) => m?.senderType === "Talent")?.sender ||
      messages.find((m) => m?.senderType === "Talent")?.senderId ||
      selectedConversation?.user;
    return other || {};
  };

  const sendReply = async () => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = replyBody;
    const text = (tempDiv.textContent || tempDiv.innerText || "").trim();
    if (!text || !selectedConversation) return;

    setSending(true);
    const other = getOtherParticipant();
    try {
      if (selectedConversation?.isSupport) {
        const res = await replySupportComplaint(selectedConversation._id, {
          message: text,
        });
        const data = res?.data;
        if (data?.success || data?.status === "success") {
          showToast("Reply sent");
          setReplyBody("");
          setShowReply(false);
          await openSupportThread(selectedConversation);
        }
        return;
      }
      const res = await clientSendMessage({
        conversationId: selectedConversation._id,
        content: replyBody,
        receiverId: other?._id,
        receiverEmail: other?.email,
        receiverType: "Talent",
      });
      if (res?.status === "success") {
        showToast("Reply sent");
        setReplyBody("");
        setShowReply(false);
        await openThread(selectedConversation, false);
      }
    } catch (error) {
      showToast("Couldn't send reply");
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSending(false);
    }
  };

  const handleDeleteConversationRow = async (e, id) => {
    e.stopPropagation();
    const ok = await confirmationHandler(
      "Delete this conversation? This cannot be undone.",
    );
    if (!ok) return;
    try {
      const res = await clientDeleteConversation(id);
      if (res?.success || res?.status === "success") {
        showToast("Conversation deleted");
        if (selectedConversation?._id === id) backToList();
        else fetchConversations(page);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleDeleteMessage = async (messageId) => {
    const ok = await confirmationHandler("Delete this message?");
    if (!ok) return;
    try {
      const res = await clientDeleteConversation(messageId);
      if (res?.status === "success") {
        setMessages((prev) => prev.filter((m) => m._id !== messageId));
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  /* ── Compose ── */
  const openCompose = () => {
    setView("compose");
    setSelectedConversation(null);
    setComposeTo("");
    setComposeToError("");
    setComposeCategory("");
    setComposeSubject("");
    setComposeBody("");
  };

  const sendCompose = async () => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = composeBody;
    const text = (tempDiv.textContent || tempDiv.innerText || "").trim();

    if (inbox === "support") {
      if (!composeSubject && !text) {
        backToList();
        return;
      }
      if (!composeSubject) {
        showToast("Subject is required");
        return;
      }
      if (!text) {
        showToast("Message content is required");
        return;
      }
      setSending(true);
      try {
        const firstName = localStorage.getItem("firstName") || "";
        const lastName = localStorage.getItem("lastName") || "";
        const email = localStorage.getItem("email") || "";
        const supportType =
          composeCategory === "Platform issue"
            ? "LogABug"
            : composeCategory === "Report behaviour"
              ? "ReportBehavior"
              : "Contact";
        const res = await postSupportComplaint({
          supportType,
          fullName: `${firstName} ${lastName}`.trim() || email,
          email,
          subject: composeSubject,
          category: composeCategory || undefined,
          message: text,
          pageOrFeature:
            supportType === "LogABug"
              ? composeCategory || composeSubject
              : undefined,
          summaryOfReport:
            supportType === "ReportBehavior" ? composeSubject : undefined,
          individualOrCompany: "Company",
        });
        const data = res?.data;
        if (data?.success || data?.status === "success") {
          showToast("Support request sent");
          backToList();
        } else {
          showToast(data?.message || "Could not send your support request");
        }
      } catch (error) {
        error?.response?.data
          ? errorHandler(error?.response?.data?.errors, "Error")
          : errorHandler(
              "Network error: Something went wrong, Please check your internet connection and try again.",
              "Error",
            );
      } finally {
        setSending(false);
      }
      return;
    }

    if (!isAllowedMessageRecipientEmail(composeTo)) {
      setComposeToError(TALENT_EMAIL_FORMAT_ERROR);
      return;
    }
    if (!composeSubject) {
      showToast("Subject is required");
      return;
    }
    if (!text) {
      showToast("Message content is required");
      return;
    }

    setSending(true);
    try {
      const convRes = await clientCreateOrGetConversation({
        participantEmail: composeTo,
        subject: composeSubject,
      });
      const conversationId = convRes?.data?.data?._id;
      if (!conversationId) {
        showToast("Failed to create conversation");
        setSending(false);
        return;
      }

      await clientSendMessage({
        conversationId,
        receiverEmail: composeTo,
        content: composeBody,
      });
      showToast("Message sent");
      backToList();
    } catch (error) {
      showToast("Failed to send message");
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSending(false);
    }
  };

  /* ── Derived stats ── */
  const unreadCount = conversations?.filter((c) => c?.unread).length;
  const todayCount = conversations?.filter(
    (c) =>
      c?.lastMessageAt && moment(c?.lastMessageAt)?.isSame(moment(), "day"),
  ).length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const truncate = (text, max = 60) =>
    text?.length > max ? `${text?.slice(0, max)}…` : text || "";

  return (
    <div className={classes.main}>
      <DocumentHeader title="Awasource | Messages" />
      <Breadcrumb
        crumbs={[
          { label: "Dashboard", href: "/client-activity-summary" },
          { label: "Messages" },
        ]}
        title="Messages"
      />

      {/* Hero */}
      <div className={classes.hero}>
        <div className={classes.heroText}>
          <div className={classes.heroEyebrow}>
            <IconMessages color="#93c5fd" size={14} /> Communication Hub
          </div>
          <div className={classes.heroTitle}>Your Inbox</div>
          <div className={classes.heroSub}>
            Manage conversations with clients and talent in one place.
          </div>
        </div>
        <div className={classes.heroStats}>
          <div className={classes.heroStat}>
            <div className={classes.heroStatValue}>{total}</div>
            <div className={classes.heroStatLabel}>Total</div>
          </div>
          <div className={classes.heroStat}>
            <div className={classes.heroStatValue}>{unreadCount}</div>
            <div className={classes.heroStatLabel}>Unread</div>
          </div>
          <div className={classes.heroStat}>
            <div className={classes.heroStatValue}>{todayCount}</div>
            <div className={classes.heroStatLabel}>Today</div>
          </div>
        </div>
      </div>

      {/* Panel */}
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <div className={classes.inboxTabs}>
            <button
              type="button"
              className={`${classes.inboxTab} ${inbox === "user" ? classes.inboxTabActive : ""}`}
              onClick={() => setInbox("user")}
            >
              <IconInbox size={15} /> User Inbox
              <span className={classes.tabBadge}>{userTotal}</span>
            </button>
            <button
              type="button"
              className={`${classes.inboxTab} ${inbox === "support" ? classes.inboxTabActive : ""}`}
              onClick={() => setInbox("support")}
            >
              <IconHeadset size={15} /> Support Inbox
              <span className={classes.tabBadge}>{supportTotal}</span>
            </button>
          </div>
        </div>

        {view === "list" && (
          <>
            <div className={classes.listToolbar}>
              <h2 className={classes.listTitle}>
                {inbox === "support" ? "Support Inbox" : "User Inbox"}
              </h2>
              <div className={classes.listToolbarRight}>
                <div className={classes.searchWrap}>
                  <IconSearch size={15} />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && fetchConversations(1)
                    }
                  />
                </div>
                <button
                  type="button"
                  className={classes.btnPrimary}
                  onClick={openCompose}
                >
                  <IconEdit size={15} /> New Message
                </button>
              </div>
            </div>

            {loading ? (
              <div className={classes.loadingSlot}>Loading conversations…</div>
            ) : conversations.length === 0 ? (
              <div className={classes.emptySlot}>
                <div className={classes.emptySlotIcon}>
                  <IconInboxOff size={28} />
                </div>
                <h3>
                  {search
                    ? "No conversations match your search"
                    : emptyCopy[inbox].title}
                </h3>
                <p>{emptyCopy[inbox].body}</p>
              </div>
            ) : (
              <>
                <div className={classes.table}>
                  <div className={classes.tableHead}>
                    <span className={classes.rowCheck}>
                      <input type="checkbox" />
                    </span>
                    <span>
                      <button
                        className={classes.sortBtn}
                        onClick={() => handleSort("title")}
                      >
                        Title {sortIcon("title")}
                      </button>
                    </span>
                    <span>
                      <button
                        className={classes.sortBtn}
                        onClick={() => handleSort("user")}
                      >
                        User {sortIcon("user")}
                      </button>
                    </span>
                    <span>
                      <button
                        className={classes.sortBtn}
                        onClick={() => handleSort("type")}
                      >
                        User Type {sortIcon("type")}
                      </button>
                    </span>
                    <span>
                      <button
                        className={classes.sortBtn}
                        onClick={() => handleSort("date")}
                      >
                        Last Updated {sortIcon("date")}
                      </button>
                    </span>
                    <span
                      style={{
                        textAlign: "right",
                        fontSize: 10.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "var(--text-muted)",
                      }}
                    >
                      Actions
                    </span>
                  </div>

                  {sortedConversations?.map((conversation) => (
                    <div
                      key={conversation?._id}
                      className={`${classes.row} ${conversation.unread ? classes.rowUnread : ""}`}
                      onClick={() => openThread(conversation)}
                    >
                      <span
                        className={classes.rowCheck}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input type="checkbox" />
                      </span>
                      <span className={classes.rowTitleCell}>
                        <span className={classes.rowTitle}>
                          {truncate(conversation?.subject, 40)}
                        </span>
                        <span
                          className={classes.rowPreview}
                          dangerouslySetInnerHTML={{
                            __html: truncate(
                              DOMPurify.sanitize(conversation?.lastMessage),
                              60,
                            ),
                          }}
                        ></span>
                      </span>
                      <span className={classes.rowUserCell}>
                        <span className={classes.rowUser}>
                          {conversation?.user?.name}
                        </span>
                        <span className={classes.rowUserEmail}>
                          {conversation?.user?.email}
                        </span>
                      </span>
                      <span className={classes.rowTypeCell}>
                        <span className={classes.typeBadge}>
                          {conversation?.user?.type}
                        </span>
                      </span>
                      <span className={classes.rowDateCell}>
                        {conversation?.lastMessageAt
                          ? moment(conversation?.lastMessageAt).format(
                              "MMM DD, YYYY",
                            )
                          : "—"}
                      </span>
                      <span className={classes.rowActions}>
                        {conversation?.isSupport ? (
                          <button
                            className={`${classes.actionBtn} ${classes.actionBtnReply}`}
                            title="View support request"
                            onClick={(e) => {
                              e.stopPropagation();
                              openThread(conversation);
                            }}
                          >
                            <IconMail size={14} />
                          </button>
                        ) : (
                          <>
                            <button
                              className={`${classes.actionBtn} ${classes.actionBtnReply}`}
                              title="Reply"
                              onClick={(e) => {
                                e.stopPropagation();
                                openThread(conversation, true);
                              }}
                            >
                              <IconArrowForwardUp size={14} />
                            </button>
                            <button
                              className={`${classes.actionBtn} ${classes.actionBtnDelete}`}
                              title="Delete"
                              onClick={(e) =>
                                handleDeleteConversationRow(e, conversation?._id)
                              }
                            >
                              <IconTrash size={14} />
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={classes.panelFooter}>
                  <span className={classes.pageInfo}>
                    {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className={classes.pageBtns}>
                    <button
                      className={classes.pageBtn}
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <IconArrowLeft size={13} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (p) => (
                        <button
                          key={p}
                          className={`${classes.pageBtn} ${p === page ? classes.pageBtnActive : ""}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      className={classes.pageBtn}
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <IconArrowForwardUp
                        size={13}
                        style={{ transform: "scaleX(-1)" }}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {view === "compose" && (
          <div className={classes.compose}>
            <button className={classes.backlink} onClick={backToList}>
              <IconArrowLeft size={15} /> Back to inbox
            </button>

            {inbox === "support" ? (
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Category</label>
                <div className={classes.formSelectWrap}>
                  <select
                    className={classes.formSelect}
                    value={composeCategory}
                    onChange={(e) => setComposeCategory(e.target.value)}
                  >
                    <option value="">Select a category…</option>
                    <option>Billing &amp; subscription</option>
                    <option>Account access</option>
                    <option>Platform issue</option>
                    <option>General inquiry</option>
                  </select>
                  <IconChevronDown size={15} />
                </div>
              </div>
            ) : (
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Email Address</label>
                <input
                  type="email"
                  className={`${classes.formInput} ${composeToError ? classes.formInputError : ""}`}
                  placeholder="name@company.com"
                  value={composeTo}
                  onChange={(e) => {
                    const value = e.target.value;
                    setComposeTo(value);
                    if (!value) {
                      setComposeToError("");
                      return;
                    }
                    if (composeToError) {
                      setComposeToError(
                        isAllowedMessageRecipientEmail(value)
                          ? ""
                          : TALENT_EMAIL_FORMAT_ERROR,
                      );
                    }
                  }}
                />
                {composeToError && (
                  <div className={classes.fieldError}>
                    <IconAlertCircle size={13} />
                    {composeToError}
                  </div>
                )}
              </div>
            )}

            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Subject</label>
              <input
                type="text"
                className={classes.formInput}
                placeholder="Message subject…"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
              />
            </div>

            <Editor
              value={composeBody}
              onChange={setComposeBody}
              placeholder="Compose email"
            />

            <div className={classes.composeFooter}>
              <div className={classes.footerLeft}>
                <button
                  type="button"
                  className={classes.btnPrimary}
                  onClick={sendCompose}
                  disabled={sending}
                >
                  <IconMail size={15} /> {sending ? "Sending…" : "Send"}
                </button>
                <BottomTools />
              </div>
              <button
                className={classes.discardBtn}
                title="Discard"
                onClick={backToList}
              >
                <IconTrash size={16} />
              </button>
            </div>
          </div>
        )}

        {view === "thread" && selectedConversation && (
          <div className={classes.compose}>
            <div className={classes.threadTopbar}>
              <button className={classes.backlink} onClick={backToList}>
                <IconArrowLeft size={15} /> Back to inbox
              </button>
            </div>
            {!selectedConversation?.isSupport && (
              <div className={classes.threadActions}>
                <button
                  className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
                  title="Delete"
                  onClick={(e) =>
                    handleDeleteConversationRow(e, selectedConversation._id)
                  }
                >
                  <IconTrash size={16} />
                </button>
              </div>
            )}

            <button
              className={`${classes.replyToggle} ${showReply ? classes.replyToggleActive : ""}`}
              onClick={() => setShowReply((p) => !p)}
            >
              <IconCornerUpLeft size={15} /> Reply
            </button>

            {showReply && (
              <div className={classes.replyBox}>
                <div className={classes.composeRecipient}>
                  <IconCornerUpLeft size={15} />{" "}
                  {selectedConversation?.isSupport
                    ? "Awasource Support"
                    : getOtherParticipant()?.email ||
                      selectedConversation?.user?.email}
                </div>
                <div className={classes.formGroup}>
                  <label className={classes.formLabel}>Subject</label>
                  <input
                    className={classes.formInput}
                    value={selectedConversation?.subject}
                    readOnly
                  />
                </div>
                <Editor
                  value={replyBody}
                  onChange={setReplyBody}
                  placeholder="Write your reply…"
                />
                <div className={classes.composeFooter}>
                  <div className={classes.footerLeft}>
                    <button
                      type="button"
                      className={classes.btnPrimary}
                      onClick={sendReply}
                      disabled={sending}
                    >
                      <IconMail size={15} /> {sending ? "Sending…" : "Send"}
                    </button>
                    <BottomTools />
                  </div>
                  <button
                    className={classes.discardBtn}
                    title="Discard"
                    onClick={() => setShowReply(false)}
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className={classes.quoteList}>
              {loading ? (
                <div className={classes.loadingSlot}>Loading messages…</div>
              ) : messages.length === 0 ? (
                <div className={classes.emptySlot}>
                  <p>No previous messages</p>
                </div>
              ) : (
                messages.map((m) => {
                  const sender = m?.sender || m?.senderId || {};
                  const receiver = m?.receiver || m?.receiverId || {};
                  const senderName =
                    `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim() ||
                    (m?.isSentByMe || m?.senderType === "Client"
                      ? "You"
                      : m?.senderType === "Support"
                        ? "Awasource Support"
                        : "Talent");
                  const receiverName =
                    `${receiver?.firstName || ""} ${receiver?.lastName || ""}`.trim() ||
                    (m?.isSentByMe || m?.senderType === "Client"
                      ? "Awasource Support"
                      : "You");
                  return (
                    <div className={classes.quoteCard} key={m?._id}>
                      <span className={classes.quoteTime}>
                        {m?.createdAt
                          ? moment(m.createdAt).format("MMM D, YYYY · h:mm A")
                          : ""}
                      </span>
                      <div className={classes.quoteMeta}>
                        <b>From:</b> {senderName} ({sender?.email || ""})
                      </div>
                      <div className={classes.quoteMeta}>
                        <b>To:</b> {receiverName} ({receiver?.email || ""})
                      </div>
                      <div className={classes.quoteMeta}>
                        <b>Subject:</b> {selectedConversation.subject}
                      </div>
                      <div
                        className={classes.quoteBody}
                        dangerouslySetInnerHTML={{ __html: m?.content || "" }}
                      />
                      {!selectedConversation?.isSupport &&
                        (m?.senderType === "Talent" || m?.isSentByMe) && (
                          <button
                            className={classes.discardBtn}
                            style={{ marginTop: 10 }}
                            title="Delete message"
                            onClick={() => handleDeleteMessage(m._id)}
                          >
                            <IconTrash size={14} />
                          </button>
                        )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className={classes.toast}>
          <IconCircleCheck size={16} />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
};

export default ClientMessagesRedesign;
